// Last line of defence against a modal layer that never finishes closing.
//
// Radix disables pointer events on <body> while a modal layer is open and
// restores them when the last one unmounts. If a layer stalls — an exit
// animation that never fires `animationend`, a portal orphaned by a
// crashed render — that restore never runs and the page stops accepting
// clicks while looking completely normal. The user's word for it was
// "the browser seems to sort of crash".
//
// Removing exit animations (components/ui/dialog.jsx) fixes the cause we
// found and could reproduce. This catches the ones we did not: it only
// ever runs when the body is already locked AND nothing is open, which
// is a state that has no legitimate reason to exist.
//
// Deliberately NOT a poller. It checks at the moments a stuck page is
// discovered — coming back to the tab, refocusing the window — because a
// timer that writes to body styles every second would be a worse bug
// than the one it fixes.

const OPEN_LAYER = [
  '[data-state="open"][role="dialog"]',
  '[data-state="open"][role="alertdialog"]',
  '[data-state="open"][role="menu"]',
  '[data-state="open"][role="listbox"]',
  '[data-slot="dialog-content"][data-state="open"]',
  '[data-slot="sheet-content"][data-state="open"]',
].join(',');

export function releaseStuckLayers(doc = document) {
  if (doc.body.style.pointerEvents !== 'none') {
    return false;
  }
  if (doc.querySelector(OPEN_LAYER)) {
    return false;
  }
  doc.body.style.pointerEvents = '';
  return true;
}

export function watchStuckLayers(target = window) {
  const doc = target.document;
  const check = () => releaseStuckLayers(doc);

  // A locked body swallows clicks on the page — but the event still
  // reaches the DOCUMENT, because pointer-events:none removes the body
  // as a hit target rather than stopping event dispatch. So the very
  // first click of somebody discovering a stuck page is also the thing
  // that unsticks it: they click, nothing appears to happen, and by the
  // next click the page works. Capture phase, and only ever acts when
  // the page is locked with nothing open.
  doc.addEventListener('pointerdown', check, true);
  doc.addEventListener('visibilitychange', check);
  target.addEventListener('focus', check);
  target.addEventListener('pageshow', check);
  return () => {
    doc.removeEventListener('pointerdown', check, true);
    doc.removeEventListener('visibilitychange', check);
    target.removeEventListener('focus', check);
    target.removeEventListener('pageshow', check);
  };
}
